// MP4 rotation + display-dimension parser.
//
// iOS records video with a 3x3 display matrix in the MP4 tkhd atom. Most
// browsers honor it at playback time, but some iOS clips that round-trip
// through Firebase Storage end up with Chromium silently ignoring the
// matrix — those play upside down or sideways.
//
// This module returns the rotation baked into the matrix AND the display
// dimensions from tkhd (which are already post-rotation per ISO/IEC 14496-12).
// The caller can compare `videoWidth`/`videoHeight` of the loaded <video>
// element against the display dimensions to detect whether Chromium honored
// the rotation on its own. If it did, the caller should skip any additional
// CSS transform to avoid double-rotating.
//
// Returns `{ rotation: 0, displayWidth: 0, displayHeight: 0 }` on any parse
// failure — callers should treat that as "no correction needed".

const CACHE = new Map();

const MOOV_HEAD_WINDOW = 512 * 1024;  // 512 KB is enough for front-loaded moov
const MOOV_TAIL_WINDOW = 1024 * 1024; // 1 MB covers most back-loaded moov

async function fetchRange(url, start, end) {
  const res = await fetch(url, {
    headers: { Range: `bytes=${start}-${end}` },
    cache: 'force-cache',
  });
  if (!res.ok && res.status !== 206) {
    throw new Error(`range fetch failed: ${res.status}`);
  }
  const contentRange = res.headers.get('Content-Range');
  let totalSize = null;
  if (contentRange) {
    const m = /\/(\d+)$/.exec(contentRange);
    if (m) totalSize = parseInt(m[1], 10);
  }
  return { view: new DataView(await res.arrayBuffer()), totalSize };
}

function atomType(view, p) {
  return String.fromCharCode(
    view.getUint8(p + 4),
    view.getUint8(p + 5),
    view.getUint8(p + 6),
    view.getUint8(p + 7),
  );
}

function readAtomHeader(view, p, end) {
  if (p + 8 > end) return null;
  let size = view.getUint32(p);
  const type = atomType(view, p);
  let headerSize = 8;
  if (size === 1) {
    if (p + 16 > end) return null;
    const hi = view.getUint32(p + 8);
    const lo = view.getUint32(p + 12);
    size = hi * 0x100000000 + lo;
    headerSize = 16;
  } else if (size === 0) {
    size = end - p;
  }
  if (size < headerSize) return null;
  return { offset: p, size, headerSize, type };
}

function findAtom(view, start, end, name) {
  let p = start;
  while (p + 8 <= end) {
    const hdr = readAtomHeader(view, p, end);
    if (!hdr) return null;
    if (hdr.type === name) return hdr;
    p += hdr.size;
  }
  return null;
}

// Convert the first two values of the tkhd matrix (16.16 signed fixed-point)
// to rotation degrees. QuickTime display matrix values:
//   0°  : a=+1.0, b= 0.0
//   90° : a= 0.0, b=+1.0
//  180° : a=-1.0, b= 0.0
//  270° : a= 0.0, b=-1.0
function matrixToDegrees(a32, b32) {
  const signed = (n) => (n >= 0x80000000 ? n - 0x100000000 : n) / 65536;
  const a = signed(a32);
  const b = signed(b32);
  const rad = Math.atan2(b, a);
  const deg = (rad * 180) / Math.PI;
  const norm = ((Math.round(deg) % 360) + 360) % 360;
  if (norm < 45 || norm >= 315) return 0;
  if (norm < 135) return 90;
  if (norm < 225) return 180;
  return 270;
}

// Walk moov → first trak whose tkhd has non-zero width/height (the video
// trak). Return { rotation, displayWidth, displayHeight }.
function extractFromMoov(view, moov) {
  const moovBodyStart = moov.offset + moov.headerSize;
  const moovEnd = moov.offset + moov.size;

  let p = moovBodyStart;
  while (p + 8 <= moovEnd) {
    const trakHdr = readAtomHeader(view, p, moovEnd);
    if (!trakHdr) break;

    if (trakHdr.type === 'trak') {
      const trakBody = trakHdr.offset + trakHdr.headerSize;
      const trakEnd = trakHdr.offset + trakHdr.size;
      const tkhd = findAtom(view, trakBody, trakEnd, 'tkhd');
      if (tkhd) {
        const bodyStart = tkhd.offset + tkhd.headerSize;
        const version = view.getUint8(bodyStart);
        // tkhd layout after 4 bytes version+flags:
        //   v0: creation(4) modification(4) track_id(4) reserved(4) duration(4) = 20
        //   v1: creation(8) modification(8) track_id(4) reserved(4) duration(8) = 32
        // Then: reserved[2]*4 + layer(2) + alt_group(2) + volume(2) + reserved(2) = 16
        // Then: 36 bytes of 3x3 matrix
        // Then: width(4, 16.16) + height(4, 16.16)
        const preMatrix = 4 + (version === 1 ? 32 : 20) + 16;
        const matrixOffset = bodyStart + preMatrix;
        const dimsOffset = matrixOffset + 36;
        if (dimsOffset + 8 > tkhd.offset + tkhd.size) {
          p += trakHdr.size;
          continue;
        }
        const a = view.getUint32(matrixOffset);
        const b = view.getUint32(matrixOffset + 4);
        const rotation = matrixToDegrees(a, b);
        // tkhd.width/height are 16.16 fixed-point AFTER the matrix has been
        // applied — so these are the intended display dimensions, already
        // accounting for rotation.
        const displayWidth = view.getUint32(dimsOffset) / 65536;
        const displayHeight = view.getUint32(dimsOffset + 4) / 65536;

        // Skip traks with zero dimensions (audio, metadata) — keep walking
        // until we find the video trak.
        if (displayWidth > 0 && displayHeight > 0) {
          return {
            rotation,
            displayWidth: Math.round(displayWidth),
            displayHeight: Math.round(displayHeight),
          };
        }
      }
    }
    p += trakHdr.size;
  }
  return { rotation: 0, displayWidth: 0, displayHeight: 0 };
}

const EMPTY = { rotation: 0, displayWidth: 0, displayHeight: 0 };

function parseView(view) {
  const moov = findAtom(view, 0, view.byteLength, 'moov');
  if (!moov) return null;
  return extractFromMoov(view, moov);
}

/**
 * Read MP4 display rotation and dimensions from a remote URL.
 * Returns { rotation, displayWidth, displayHeight }. Failures resolve to
 * zeros so callers don't need to wrap in try/catch.
 */
export async function getMp4Rotation(url) {
  if (!url || typeof url !== 'string') return EMPTY;
  if (CACHE.has(url)) return CACHE.get(url);

  try {
    // First try the head — iOS writes moov at the front, so this hits for
    // ~all chat clips.
    const head = await fetchRange(url, 0, MOOV_HEAD_WINDOW - 1);
    let result = parseView(head.view);

    // Fallback: moov-at-end (common for Android re-muxes). Read the tail if
    // we know the total size from Content-Range.
    if (!result && head.totalSize && head.totalSize > MOOV_HEAD_WINDOW) {
      const start = Math.max(0, head.totalSize - MOOV_TAIL_WINDOW);
      const tail = await fetchRange(url, start, head.totalSize - 1);
      result = parseView(tail.view);
    }

    const out = result || EMPTY;
    CACHE.set(url, out);
    return out;
  } catch (err) {
    console.warn('[mp4 rotation] parse failed for', url, err);
    CACHE.set(url, EMPTY);
    return EMPTY;
  }
}

export default { getMp4Rotation };
